import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../config";
import ConfirmDialog from "../../components/ConfirmDialog";
import useConfirmDialog from "../../hooks/useConfirmDialog";

const initialUserForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  role: "user",
};

export default function OpsCompanies() {
  const token = localStorage.getItem("userToken");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const role = (localStorage.getItem("userRole") || "").toLowerCase();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [impersonating, setImpersonating] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [deviceStatus, setDeviceStatus] = useState("");
  const [userStatus, setUserStatus] = useState("");
  const [alertModal, setAlertModal] = useState({ device: null, alerts: [], loading: false });
  const [addUserModal, setAddUserModal] = useState(false);
  const [addUserForm, setAddUserForm] = useState(initialUserForm);
  const { confirmProps, openConfirm } = useConfirmDialog();

  const loadCompanies = async (query) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/ops/companies`, {
        headers,
        params: query ? { search: query } : {},
      });
      setCompanies(response.data);
      setError("");
    } catch (err) {
      console.error("OpsCompanies load error", err.response?.data || err.message);
      setError("Unable to load companies.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (event) => {
    event.preventDefault();
    loadCompanies(search);
  };

  const handleDeleteCompany = async (companyId) => {
    if (role !== "superadmin") return;
    try {
      await axios.delete(`${API_BASE_URL}/ops/companies/${companyId}`, { headers });
      if (detail?.id === companyId) {
        setDetail(null);
        setDevices([]);
        setUsers([]);
      }
      loadCompanies(search);
    } catch (err) {
      console.error("OpsCompanies delete error", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to delete company.");
    }
  };

  const confirmDeleteCompany = (company) => {
    if (role !== "superadmin") return;
    openConfirm({
      title: "Delete Company",
      message: `Delete ${company.name}? This removes all devices, users, and activity permanently.`,
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: () => handleDeleteCompany(company.id),
    });
  };

  const handleImpersonate = async (company) => {
    if (localStorage.getItem("impersonatedCompanyName")) {
      setError("You are already impersonating a company. Exit that session first.");
      return;
    }
    setImpersonating(true);
    setError("");
    try {
      const originalSession = {
        token: localStorage.getItem("userToken"),
        role: localStorage.getItem("userRole"),
      };
      sessionStorage.setItem("opsOriginSession", JSON.stringify(originalSession));

      const response = await axios.post(
        `${API_BASE_URL}/ops/companies/${company.id}/impersonate`,
        { userId: null },
        { headers }
      );

      localStorage.setItem("userToken", response.data.token);
      localStorage.setItem("userRole", response.data.user.role);
      localStorage.setItem("impersonatedCompanyName", company.name);
      localStorage.setItem("impersonatedCompanyId", company.id);
      localStorage.setItem("impersonatedUserEmail", response.data.user.email);
      localStorage.setItem("impersonatedUserId", response.data.user.user_id);

      navigate("/dashboard");
    } catch (err) {
      console.error("OpsCompanies impersonate error", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to open portal for this company.");
    } finally {
      setImpersonating(false);
    }
  };

  const copyBillingPortalLink = async (companyId) => {
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/ops/companies/${companyId}/portal-link`,
        {},
        { headers }
      );
      await navigator.clipboard.writeText(data.url);
      setActionMessage("Portal link copied to clipboard.");
      setTimeout(() => setActionMessage(""), 2500);
    } catch (err) {
      console.error("OpsCompanies portal link error", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to create billing portal link.");
    }
  };

  const selectCompany = async (companyId) => {
    try {
      setDetailLoading(true);
      const [profileRes, devicesRes, usersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/ops/companies/${companyId}/profile`, { headers }),
        axios.get(`${API_BASE_URL}/ops/companies/${companyId}/devices`, { headers }),
        axios.get(`${API_BASE_URL}/ops/companies/${companyId}/users`, { headers }),
      ]);

      setDetail(profileRes.data);
      setDevices(devicesRes.data);
      setUsers(usersRes.data);
      setDeviceStatus("");
      setUserStatus("");
      setError("");
    } catch (err) {
      console.error("OpsCompanies detail error", err.response?.data || err.message);
      setError("Unable to load company detail.");
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = () => {
    if (detail) selectCompany(detail.id);
  };

  const handleDisableDevice = async (device) => {
    const reason = window.prompt("Reason for disabling this device?", "Ops action");
    if (reason === null) return;
    try {
      await axios.post(
        `${API_BASE_URL}/ops/devices/${device.device_fingerprint}/disable`,
        { reason },
        { headers }
      );
      setDeviceStatus("Device disabled.");
      refreshDetail();
    } catch (err) {
      console.error("OpsCompanies disable device error", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to disable device.");
    }
  };

  const handleEnableDevice = async (device) => {
    try {
      await axios.post(`${API_BASE_URL}/ops/devices/${device.device_fingerprint}/enable`, {}, { headers });
      setDeviceStatus("Device re-enabled.");
      refreshDetail();
    } catch (err) {
      console.error("OpsCompanies enable device error", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to enable device.");
    }
  };

  const handleHeartbeatRequest = async (device) => {
    try {
      await axios.post(
        `${API_BASE_URL}/ops/devices/${device.device_fingerprint}/request-heartbeat`,
        {},
        { headers }
      );
      setDeviceStatus("Heartbeat request sent.");
    } catch (err) {
      console.error("OpsCompanies heartbeat request error", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to request heartbeat.");
    }
  };

  const openAlertModal = async (device) => {
    setAlertModal({ device, alerts: [], loading: true });
    try {
      const { data } = await axios.get(`${API_BASE_URL}/ops/devices/${device.device_fingerprint}/alerts`, {
        headers,
      });
      setAlertModal({ device, alerts: data, loading: false });
    } catch (err) {
      console.error("OpsCompanies alerts error", err.response?.data || err.message);
      setAlertModal({ device, alerts: [], loading: false });
      setError("Unable to load device alerts.");
    }
  };

  const closeAlertModal = () => setAlertModal({ device: null, alerts: [], loading: false });

  const openAddUserModal = () => {
    setAddUserForm(initialUserForm);
    setAddUserModal(true);
  };
  const closeAddUserModal = () => setAddUserModal(false);

  const submitAddUser = async () => {
    if (!detail) return;
    try {
      await axios.post(`${API_BASE_URL}/ops/companies/${detail.id}/users`, addUserForm, { headers });
      setUserStatus("User invited via email.");
      closeAddUserModal();
      refreshDetail();
    } catch (err) {
      console.error("OpsCompanies add user error", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to add user.");
    }
  };

  const handleUserAction = async (user, action) => {
    try {
      if (action === "verification") {
        await axios.post(`${API_BASE_URL}/ops/users/${user.user_id}/resend-verification`, {}, { headers });
        setUserStatus("Verification email sent.");
      } else if (action === "reset") {
        await axios.post(`${API_BASE_URL}/ops/users/${user.user_id}/reset-password`, {}, { headers });
        setUserStatus("Password reset email sent.");
      }
    } catch (err) {
      console.error("OpsCompanies user action error", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to complete action.");
    }
  };

  return (
    <>
      <div className="ops-companies">
      <div className="ops-section">
        <div className="ops-section-header">
          <h3>Companies</h3>
          <form onSubmit={handleSearch} className="ops-search">
            <input
              type="text"
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn btn-primary" type="submit">
              Search
            </button>
          </form>
        </div>
        {error && <p className="text-danger">{error}</p>}
        {loading ? (
          <p>Loading companies...</p>
        ) : (
          <div className="ops-table-wrapper">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company ID</th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Devices</th>
                  <th>Users</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id}>
                    <td>{company.name}</td>
                    <td className="mono-id">{company.id}</td>
                    <td>{company.email}</td>
                    <td>{company.plan_name || "Unassigned"}</td>
                    <td>{company.total_devices}</td>
                    <td>{company.total_users}</td>
                    <td>{new Date(company.created_at).toLocaleDateString()}</td>
                    <td className="d-flex gap-2 flex-wrap">
                      <button className="btn btn-sm btn-outline-primary" onClick={() => selectCompany(company.id)}>
                        View
                      </button>
                      {role === "superadmin" && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                        onClick={() => confirmDeleteCompany(company)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <div className="ops-section">
          <div className="ops-section-header flex-wrap">
            <div>
              <h3>Details: {detail.name}</h3>
              {actionMessage && <span className="text-success">{actionMessage}</span>}
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {detailLoading && <span className="text-muted me-3">Refreshing...</span>}
              <button
                className="btn btn-outline-secondary"
                onClick={() => handleImpersonate(detail)}
                disabled={impersonating}
              >
                {impersonating ? "Starting session..." : "Open Client Portal"}
              </button>
              <button className="btn btn-outline-primary" onClick={() => copyBillingPortalLink(detail.id)}>
                Copy Billing Portal Link
              </button>
              <button className="btn btn-outline-dark" onClick={openAddUserModal}>
                Add User
              </button>
            </div>
          </div>
          <div className="ops-detail-grid">
            <div className="ops-detail-card">
              <strong>Company Info</strong>
              <p>
                ID: <span className="mono-id">{detail.id}</span>
              </p>
              <p>Email: {detail.email}</p>
              <p>Plan: {detail.plan_name || "Unassigned"}</p>
              <p>Created: {new Date(detail.created_at).toLocaleString()}</p>
            </div>
            <div className="ops-detail-card">
              <strong>Plan Limits</strong>
              <p>Devices allowed: {detail.devices_allowed ?? "â€”"}</p>
              <p>Users allowed: {detail.users_allowed ?? "â€”"}</p>
              <p>Price: {detail.price ? `$${detail.price}` : "â€”"}</p>
            </div>
          </div>
          <div className="ops-detail-grid" style={{ marginTop: 16 }}>
            <div className="ops-detail-card">
              <strong>Devices ({devices.length})</strong>
              <div className="ops-table-wrapper">
                <table className="ops-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Heartbeat</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.slice(0, 6).map((device) => (
                      <tr key={device.device_fingerprint}>
                        <td>{device.device_name || device.device_fingerprint}</td>
                        <td>
                          {device.status}
                          {device.disabled_at && (
                            <span className="d-block text-muted">
                              Disabled {new Date(device.disabled_at).toLocaleString()}
                            </span>
                          )}
                        </td>
                        <td>
                          {device.last_heartbeat_at
                            ? new Date(device.last_heartbeat_at).toLocaleString()
                            : "â€”"}
                        </td>
                        <td className="d-flex gap-2 flex-wrap">
                          {device.status === "disabled" ? (
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleEnableDevice(device)}
                            >
                              Enable
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDisableDevice(device)}
                            >
                              Disable
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleHeartbeatRequest(device)}
                          >
                            Ping
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => openAlertModal(device)}
                          >
                            Alerts
                          </button>
                        </td>
                      </tr>
                    ))}
                    {devices.length === 0 && (
                      <tr>
                        <td colSpan={4}>No devices found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {deviceStatus && <p className="text-success mt-2">{deviceStatus}</p>}
            </div>
            <div className="ops-detail-card">
              <strong>Users ({users.length})</strong>
              <div className="ops-table-wrapper">
                <table className="ops-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Verified</th>
                      <th>Role</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.slice(0, 6).map((user) => (
                      <tr key={user.user_id}>
                        <td>{`${user.first_name} ${user.last_name}`}</td>
                        <td>{user.email}</td>
                        <td>{user.email_verified ? "Yes" : "No"}</td>
                        <td>{user.role}</td>
                        <td className="d-flex gap-2 flex-wrap">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleUserAction(user, "verification")}
                          >
                            Resend Verification
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleUserAction(user, "reset")}
                          >
                            Reset Password
                          </button>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={5}>No users found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {userStatus && <p className="text-success mt-2">{userStatus}</p>}
            </div>
          </div>
        </div>
      )}

      {alertModal.device && (
        <div className="modal-overlay">
          <div className="modal-content">
            <span className="close-icon" onClick={closeAlertModal}>
              Ã—
            </span>
            <h5>
              Alerts for {alertModal.device.device_name || alertModal.device.device_fingerprint}
            </h5>
            {alertModal.loading ? (
              <p>Loading alerts...</p>
            ) : alertModal.alerts.length === 0 ? (
              <p>No alerts found.</p>
            ) : (
              <table className="ops-table mt-3">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Resolved</th>
                  </tr>
                </thead>
                <tbody>
                  {alertModal.alerts.map((alert) => (
                    <tr key={alert.id}>
                      <td>{alert.alert_type}</td>
                      <td>{alert.status}</td>
                      <td>{new Date(alert.created_at).toLocaleString()}</td>
                      <td>{alert.resolved_at ? new Date(alert.resolved_at).toLocaleString() : "â€”"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {addUserModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <span className="close-icon" onClick={closeAddUserModal}>
              Ã—
            </span>
            <h5>Add User to {detail?.name}</h5>
            <div className="modal-grid" style={{ marginTop: 12 }}>
              <label>
                First name
                <input
                  value={addUserForm.first_name}
                  onChange={(e) => setAddUserForm((prev) => ({ ...prev, first_name: e.target.value }))}
                />
              </label>
              <label>
                Last name
                <input
                  value={addUserForm.last_name}
                  onChange={(e) => setAddUserForm((prev) => ({ ...prev, last_name: e.target.value }))}
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  value={addUserForm.email}
                  onChange={(e) => setAddUserForm((prev) => ({ ...prev, email: e.target.value }))}
                />
              </label>
              <label>
                Phone
                <input
                  value={addUserForm.phone}
                  onChange={(e) => setAddUserForm((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </label>
              <label>
                Role
                <select
                  value={addUserForm.role}
                  onChange={(e) => setAddUserForm((prev) => ({ ...prev, role: e.target.value }))}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={submitAddUser}>
                Invite User
              </button>
              <button className="btn btn-outline-secondary" onClick={closeAddUserModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      <ConfirmDialog {...confirmProps} />
    </>
  );
}

